import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {t} from 'i18next';
import {ReactComponent as DownloadIcon} from '../../../assests/svg/download-icon.svg';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import AurButton from '../../common/aur-button/AurButton';
import BarChartCell from '../../reports/HostingVolumeReport/BarChartCell';
import {
  downloadDocTypeCsv,
  getDocTypes,
} from '../../../api/EcaManagementService';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {
  downloadResponseAsCSV,
  getProjectReportFileName,
} from '../../../utils/utils';
import {useNavigate} from 'react-router-dom';
import {FileType} from '../../eca-doc-list/eca-doc-list.util';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
interface DocTypesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string | null | undefined;
  loadFileId?: number;
  projectName?: string | null;
}

interface DocType {
  docTypeId: string;
  name: string;
  documentCount: number;
}

const DocTypesDialog: React.FC<DocTypesDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  loadFileId,
  projectName,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {trackGAEvent} = useAnalytics();

  const [docTypeData, setDocTypeData] = useState<DocType[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);
  const [docTypeCount, setDocTypeCount] = useState(0);
  const [isClearAllLoading, setIsClearAllLoading] = useState<boolean>(false);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const navigate = useNavigate();
  const {hasSearchesPerm} = usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);

  const docTypeTranslations: Record<string, string> = {
    [FileType.CALENDER_ENTRY]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.calendarEntry'
    ),
    [FileType.CHAT_MESSAGES]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.chatMessages'
    ),
    [FileType.CHAT_CONVERSATIONS]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.chatConversations'
    ),
    [FileType.CONTAINER]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.container'
    ),
    [FileType.CONTACT]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.contact'
    ),
    [FileType.DATABASE]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.database'
    ),
    [FileType.DOCUMENT]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.document'
    ),
    [FileType.WORD_PROCESSOR_DOCUMENT]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.wordProcessorDocument'
    ),
    [FileType.DRAWING]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.drawing'
    ),
    [FileType.EMAIL]: t('ecaHomepageModals.docTypeModal.docTypeNames.email'),
    [FileType.IMAGE]: t('ecaHomepageModals.docTypeModal.docTypeNames.image'),
    [FileType.LOG]: t('ecaHomepageModals.docTypeModal.docTypeNames.log'),
    [FileType.MULTIMEDIA]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.multimedia'
    ),
    [FileType.NO_DATA]: t('ecaHomepageModals.docTypeModal.docTypeNames.noData'),
    [FileType.OTHER_DOCUMENT]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.otherDocument'
    ),
    [FileType.PRESENTATION]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.presentation'
    ),
    [FileType.SPREADSHEET]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.spreadsheet'
    ),
    [FileType.SYSTEM_FILE]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.systemFile'
    ),
    [FileType.SYSTEM]: t('ecaHomepageModals.docTypeModal.docTypeNames.system'),
    [FileType.UNRECOGNIZED]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.unrecognized'
    ),
    [FileType.UNKNOWN]: t(
      'ecaHomepageModals.docTypeModal.docTypeNames.unknown'
    ),
  };

  const fetchDocTypes = async () => {
    setLoading(true);
    setIsFailedToLoadContent(false);
    try {
      let paramString = `clientId=${clientId}&projectId=${projectId}`;
      if (loadFileId) {
        paramString += `&loadFilesId=${loadFileId}`;
      }

      const response = await getDocTypes(paramString);
      const res = response.data;
      const data = res?.data || [];
      const sortedData = [...data].sort(
        (a: DocType, b: DocType) => b.documentCount - a.documentCount
      );
      setDocTypeData([...sortedData]);
      setDocTypeCount(data.length);
    } catch (error) {
      console.warn(error);
      setIsFailedToLoadContent(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDocTypes();
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedRows([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      handleDownloadCDocTypeCsv();
    }
  }, []);

  const handleDownloadCDocTypeCsv = async () => {
    setIsClearAllLoading(true);
    try {
      let paramString = `clientId=${clientId}&projectId=${projectId}&SortColumn=doctypecount&SortOrder=desc`;
      if (loadFileId) {
        paramString += `&loadFilesId=${loadFileId}`;
      }
      const response = await downloadDocTypeCsv(paramString);
      downloadResponseAsCSV(
        response,
        getProjectReportFileName(
          projectName,
          t('ecaHomepageModals.docTypeModal.docTypeHeading')
        )
      );
    } catch (e) {
      console.log(e);
    } finally {
      setIsClearAllLoading(false);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
        file_extension: GA_FILE_EXTENTIONS.CSV,
        click: {
          name: t('gaTracking.global.downloadReport'),
          region: GA_REGIONS.MODAL,
          type: GA_CLICK_TYPES.BUTTON,
          action: t('gaTracking.global.downloadedReport'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
            label: t('gaTracking.ecaHomePage.docTypeHeading'),
          }),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  };
  const rows = docTypeData?.map((item, ind) => {
    const normalizedName = item.name.toLowerCase();
    return {
      id: ind,
      docTypeId: ind,
      documentName: docTypeTranslations[normalizedName] || item.name,
      documentCount: item.documentCount,
    };
  });

  const columns: any = [
    {
      field: 'documentName',
      headerName: t('column.document'),
      width: 200,
      sortable: false,
      flex: 0.5,
      isBorderNotRequired: true,
      headerClassName: 'no-separator',
    },
    {
      field: 'documentCount',
      headerName: t('column.documentCount'),
      minWidth: 200,
      sortable: false,
      flex: 1.5,
      headerClassName: 'no-separator',
      renderCell: (params: any) => (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <BarChartCell
            value={params.row.documentCount}
            maxValue={Math.max(
              ...(docTypeData?.map((d) => d.documentCount) || [])
            )}
            name="docValue"
            showLabel={true}
            fullWidthBar={true}
            showTooltip={false}
            theme="light"
          />
        </Box>
      ),
    },
  ];

  const handleSelectionChange = (newSelection: any) => {
    setSelectedRows(newSelection);
  };

  const renderModalHeader = () => (
    <Typography className="typo-text headline3_size lightWhite-color" id="doc-type-modal-title">
      {t('ecaHomepageModals.docTypeModal.docTypeHeading')}
    </Typography>
  );

  const renderHeaderActions = () => (
    <Box className="header-alignment">
      <Box className="modal-count-header">
        <Typography className="typo-text display3_size lightWhite-color">
          {docTypeCount}
        </Typography>
        <Typography className="typo-text subhead4_size">
          {t('ecaHomepageModals.docTypeModal.typesText')}
        </Typography>
      </Box>
      <Box>
        <AurButton
          data-testid="export-button"
          onClick={handleDownloadCDocTypeCsv}
          startIcon={
            isClearAllLoading ? (
              <div className="color-loader">
                <AurLoadingIndicator
                  size={16}
                  className="progress-icon"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <DownloadIcon aria-hidden="true" />
            )
          }
          type="continue"
          size="Medium"
          disabled={isClearAllLoading}
          label={t('common.downloadReport')}
          classNames="typo-text buttonMedium_size"
        />
      </Box>
    </Box>
  );

  const renderTable = () => (
    <div tabIndex={0} className="table-data space-top-30 show-scrollbar">
      <AurDataGrid
        getRowId={(item) => item?.id}
        rows={rows}
        columns={columns}
        showPagination={false}
        visibleToolTip={true}
        columnHeaderHeight={0}
        onSelectionChange={handleSelectionChange}
        noRowsOverlay={
          isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
        }
        currentPage={1}
        itemsPerPage={rows?.length ?? 100}
        checkboxSelection={hasSearchesPerm}
        disableRowSelectionOnClick={!hasSearchesPerm}
        addedInDialog
        classNameOfParentScollElement='table-data'
      />
    </div>
  );

  const renderDialogActions = () => (
    <DialogActions className="footer-action-container">
      <div style={{display: 'flex', gap: '24px', marginBlock: '10px'}}>
        <Stack>
          <AurButton
            onClick={onClose}
            type="exit"
            size="Medium"
            label={t('ecaHomepageModals.docTypeModal.closeText')}
            classNames="pagination-cancel-button"
            dataTestId="pagination-cancel-button"
          />
        </Stack>
        {hasSearchesPerm && (
          <Stack>
            <AurButton
              type="continue"
              size="Medium"
              label={t('ecaHomepageModals.docTypeModal.ViewInDoc')}
              disabled={selectedRows?.length === 0}
              classNames="pagination-continue-button"
              dataTestId="pagination-continue-button"
              onClick={() => {
                const selectedData = rows?.filter((item) =>
                  selectedRows.includes(item.docTypeId)
                );
                navigate('/index-search', {
                  state: {
                    source: 'docType',
                    selected: selectedData,
                  },
                });
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.SEARCH,
                  click: {
                    name: t('gaTracking.ecaHomePage.ViewInDoc'),
                    region: GA_REGIONS.MODAL,
                    type: GA_CLICK_TYPES.BUTTON,
                    action: t('gaTracking.ecaHomePage.searchDocuments'),
                  },
                  subPage: {
                    subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
                      label: t('gaTracking.ecaHomePage.docTypeHeading'),
                    }),
                    subpage_type: GA_SUBPAGE_TYPE.MODAL,
                  },
                });
              }}
            />
          </Stack>
        )}
      </div>
    </DialogActions>
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onClose}
      PaperProps={{className: 'evidence-source-dialog'}}
      maxWidth={false}
      aria-labelledby='doc-type-modal-title'
    >
      <DialogContent className="model-content">
        {renderModalHeader()}
        {renderHeaderActions()}
        {renderTable()}
      </DialogContent>
      {renderDialogActions()}
      <AurLoadingIndicator overlay={true} isOpen={loading} />
    </Dialog>
  );
};

export default DocTypesDialog;
